import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { store } from './store';
import { fetchCart } from './store/cartSlice';

// Pages
import HomePage from './pages/HomePage';
import ProductPage from './pages/ProductPage';
import ProductDetailPage from './pages/ProductDetailPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import LoginPage from './pages/LoginPage';
import ForgotPassword from './pages/ForgotPassword';
import RegisterPage from './pages/RegisterPage';
import About from './pages/About';
import ContactPage from './pages/ContactPage';
import WishlistPage from './pages/WishlistPage';
import OrdersPage from './pages/OrdersPage';
import AccountPage from './pages/AccountPage';
import FAQPage from './pages/FAQPage';
import Terms from './pages/Terms';
import PrivacyPolicy from './pages/PrivacyPolicy';
import ReturnsExchange from './pages/ReturnsExchange';
import ShippingPolicy from './pages/ShippingPolicy';

// Admin Components (from components/admin path)
import AdminLayout from './components/admin/AdminLayout';
import AdminDashboard from './components/admin/AdminDashboard';
import AdminOrders from './components/admin/AdminOrders';
import AdminCategories from './components/admin/AdminCategories';
import AdminProducts from './components/admin/AdminProducts';
import AdminCustomers from './components/admin/AdminCustomers';
import AdminLensOptions from './components/admin/AdminLensOptions';
import AdminCoupons from './components/admin/AdminCoupons';
import AdminReviews from './components/admin/AdminReviews';
import AdminCart from './components/admin/AdminCart';
import AdminReports from './components/admin/AdminReports';
import AdminRoute from './components/admin/AdminRoute';

// Components
import Header from './components/common/Header';
import Footer from './components/common/Footer';

// Styles
import './index.css';

const queryClient = new QueryClient();

function AppContent() {
  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector((state) => state.auth);

  // Fetch cart when user logs in
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchCart());
    }
  }, [isAuthenticated, dispatch]);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-grow">
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/products" element={<ProductPage />} />
          <Route path="/products/:id" element={<ProductDetailPage />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/wishlist" element={<WishlistPage />} />
          <Route path="/orders" element={<OrdersPage />} />
          <Route path="/account" element={<AccountPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/terms" element={<Terms />} />
<Route path="/privacy" element={<PrivacyPolicy />} />
<Route path="/returns" element={<ReturnsExchange />} />
<Route path="/shipping" element={<ShippingPolicy />} />
          
          {/* Admin Routes */}
          <Route path="/admin" element={
            <AdminRoute>
              <AdminLayout />
            </AdminRoute>
          }>
            <Route index element={<AdminDashboard />} />
            <Route path="orders" element={<AdminOrders />} />
            <Route path="products" element={<AdminProducts />} />
            <Route path="categories" element={<AdminCategories />} />
            <Route path="customers" element={<AdminCustomers />} />
            <Route path="lens-options" element={<AdminLensOptions />} />
            <Route path="coupons" element={<AdminCoupons />} />
            <Route path="reviews" element={<AdminReviews />} />
            <Route path="cart" element={<AdminCart />} />
            <Route path="reports" element={<AdminReports />} />
          </Route>
          
          {/* 404 Page */}
          <Route path="*" element={
            <div className="container py-16 text-center">
              <h1 className="text-4xl font-bold mb-4">404</h1>
              <p className="text-gray-600 mb-8">Page not found</p>
              <Link to="/" className="btn-primary">Go Home</Link>
            </div>
          } />
        </Routes>
      </main>
      <Footer />
    </div>
  );
}

function App() {
  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <Router>
          <AppContent />
        </Router>
      </QueryClientProvider>
    </Provider>
  );
}

export default App;